'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { UserProfile, toUserProfile, UserProfileRow } from '@/types';

interface AuthContextValue {
  user: any | null;
  session: any | null;
  userProfile: UserProfile | null;
  loading: boolean;
  profileLoading: boolean;
  authInitError: boolean;
  supabase: ReturnType<typeof createClient>;
  signUp: (email: string, password: string, fullName: string) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (updates: { fullName?: string; avatarUrl?: string }) => Promise<void>;
  refreshProfile: () => Promise<void>;
  retryAuthInit: () => void;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [authInitError, setAuthInitError] = useState(false);

  // Stable Supabase client — created once at mount, never recreated on re-render.
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  // Track in-flight fetch to avoid redundant concurrent fetches
  const fetchingRef = useRef(false);

  const fetchProfile = useCallback(async (userId: string) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setProfileLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (!error && data) {
        setUserProfile(toUserProfile(data as UserProfileRow));
      }
    } catch {
      // Profile fetch failed silently
    } finally {
      fetchingRef.current = false;
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let settled = false;

    // Safety timeout: if getSession() never resolves (expired token + Supabase unreachable),
    // signal authInitError after 8 s. loading stays true — this is NOT a confirmed signed-out
    // state. onAuthStateChange remains active so recovery is automatic when Supabase returns.
    const timeoutId = setTimeout(() => {
      if (!mounted || settled) return;
      setAuthInitError(true);
    }, 8000);

    supabase.auth.getSession().then(({ data: { session } }) => {
      clearTimeout(timeoutId);
      if (!mounted) return;
      settled = true;
      setAuthInitError(false);
      setSession(session);
      setUser(session?.user ?? null);
      // Unblock the app immediately — pages can render as soon as auth state is known.
      // profileLoading remains true until fetchProfile resolves separately.
      setLoading(false);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfileLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      // loading is already false after the initial getSession() resolves.
      // For subsequent auth state changes (sign-in, token refresh, sign-out)
      // we update user/session synchronously and let profileLoading track the profile.
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setUserProfile(null);
        setProfileLoading(false);
      }
    });

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  // Re-attempt getSession() after a timeout/network failure.
  // Does NOT create a new auth listener — the existing onAuthStateChange subscription
  // remains active and will handle the session once Supabase responds.
  const retryAuthInit = useCallback(() => {
    setAuthInitError(false);
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfileLoading(false);
      }
    });
  }, [supabase, fetchProfile]);

  const signUp = async (email: string, password: string, fullName: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName, role: 'parent' },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUserProfile(null);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/auth/reset-password`,
    });
    if (error) throw error;
  };

  const updateProfile = async (updates: { fullName?: string; avatarUrl?: string }) => {
    if (!user) throw new Error('Not authenticated');
    const { error } = await supabase
      .from('user_profiles')
      .update({
        ...(updates.fullName !== undefined && { full_name: updates.fullName }),
        ...(updates.avatarUrl !== undefined && { avatar_url: updates.avatarUrl }),
      })
      .eq('id', user.id);
    if (error) throw error;
    await fetchProfile(user.id);
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  const value: AuthContextValue = {
    user,
    session,
    userProfile,
    loading,
    profileLoading,
    authInitError,
    supabase,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
    refreshProfile,
    retryAuthInit,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
